import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faDownload, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { getRequest } from "../../utils/axios";
import { NotificationManager } from "react-notifications";
import moment from "moment";

import MainA from "../../layout/Main Admin";
import Button from "../../components/atoms/Button";
import Loader from "../../components/atoms/Loader";
import Table from "../../components/atoms/Table";
import Modal from "../../components/atoms/Modal";
import CustomDropdown from "../../components/atoms/CustomDropdown";
import Input from "../../components/atoms/Input";

import "./styles.scss";

const ReelRequest = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState([]);
  const [sortColumn, setSortColumn] = useState("requestType");
  const [sortOrder, setSortOrder] = useState("asc");
  const [eventData, setEventData] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState([]);
  const [isReelModalOpen, setIsReelModalOpen] = useState(false);
  const [signedReels, setSignedReels] = useState({});
  const [expandedReelKey, setExpandedReelKey] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImageRequest, setSelectedImageRequest] = useState(null);
  const [signedImages, setSignedImages] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState({
    label: "All Events",
    value: "",
  });
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchText, setSearchText] = useState("");


  useEffect(() => {
    fetchRequests();
    getEvents();
  }, []);

  useEffect(() => {
    if (isReelModalOpen && selectedRequest?.reels?.length > 0) {
      generateSignedUrls();
    }
  }, [isReelModalOpen]);

  useEffect(() => {
    if (isImageModalOpen && selectedImageRequest?.imageS3Keys?.length > 0) {
      generateImageSignedUrls();
    }
  }, [isImageModalOpen]);


  const fetchRequests = async () => {
    try {
      setLoading(true);

      const [requestResp, reelResp] = await Promise.all([
        getRequest("EventRequest/GetAll"),
        getRequest("EventReel/GetAllReels"),
      ]);

      const requests = requestResp.data || [];
      const reels = reelResp.data || [];

      const merged = requests.map((req) => ({
        ...req,
        reels: reels.filter((reel) => reel.requestId === req.requestId),
      }));

      setRequestData(merged);
      setFilteredRequests(merged);
    } catch (error) {
      console.error(error);
      NotificationManager.error("Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  const getEvents = async () => {
    try {
      const resp = await getRequest(
        `Page/GetPagesByCategoryType/${categoryId}`
      );
      setEventData(resp.data);
    } catch (error) {
      console.error("Error fetching event:", error);
    }
  };

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    setSortColumn(column);
    setSortOrder(order);

    const sorted = [...requestData].sort((a, b) => {
      const aVal = a[column] || "";
      const bVal = b[column] || "";
      return order === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });

    setRequestData(sorted);
  };

  const handleOpenReelModal = (item) => {
    if (!item.reels || item?.reels?.length === 0) {
      NotificationManager.info("No reels available");
      return;
    }
    setSelectedRequest(item);
    setIsReelModalOpen(true);
  };

  const RequestType = {
    GENERATE_EVENT_REELS: "GENERATE REELS",
    PROCESS_EVENT_IMAGES: "PROCESS IMAGES",
    PROCESS_UNPROCESSED_IMAGES_BY_FACE: "SEGREGATE IMAGES (FACE)",
  };

  const generateSignedUrls = async () => {
    try {
      const urls = {};

      await Promise.all(
        selectedRequest.reels.map(async (reel) => {
          if (!reel.reelPath) return;

          const resp = await getRequest(
            `S3/GetPreSignedUrl?key=${encodeURIComponent(reel.reelPath)}`
          );

          urls[reel.reelPath] = resp.data.url;
        })
      );

      setSignedReels(urls);
    } catch (error) {
      console.error("Failed to generate signed URLs", error);
    }
  };

  const handlePlayClick = (reelPath) => {
    setExpandedReelKey((prev) => (prev === reelPath ? null : reelPath));
  };

  const handleOpenImageModal = (item) => {
    if (!item.imageS3Keys || item.imageS3Keys.length === 0) {
      NotificationManager.info("No images available");
      return;
    }

    setSelectedImageRequest(item);
    setIsImageModalOpen(true);
  };
  
  const generateImageSignedUrls = async () => {
    try {
      const urls = {};

      await Promise.all(
        selectedImageRequest.imageS3Keys.map(async (key) => {
          const resp = await getRequest(
            `S3/GetPreSignedUrl?key=${encodeURIComponent(key)}`
          );
          urls[key] = resp.data.url;
        })
      );

      setSignedImages(urls);
    } catch (error) {
      console.error("Failed to generate image URLs", error);
    }
  };

  const handleEventFilter = (eventId) => {
    const selected =
      eventId
        ? {
            label:
              eventData.find((e) => String(e.pageId) === String(eventId))
                ?.pageName || "Event",
            value: eventId,
          }
        : { label: "All Events", value: "" };

    setSelectedEvent(selected);
    applyFilters(eventId, searchText);
  };


  const applyFilters = (eventId, text) => {
    let data = [...requestData];

    if (eventId) {
      data = data.filter(
        (item) => String(item.eventId) === String(eventId)
      );
    }

    if (text) {
      const lower = text.toLowerCase();

      data = data.filter((item) => {
        const eventName =
          eventData.find((e) => e.pageId === item.eventId)?.pageName || "";

        return (
          String(item.requestId).includes(lower) ||
          (item.bibId && item.bibId.toLowerCase().includes(lower)) ||
          item.status?.toLowerCase().includes(lower) ||
          RequestType[item.requestType]?.toLowerCase().includes(lower) ||
          eventName.toLowerCase().includes(lower)
        );
      });
    }

    setFilteredRequests(data);
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    applyFilters(selectedEvent.value, value);
  };


  return (
    <MainA className="b-event-reel-request">
      {loading && <Loader loading={loading} />}

      <Modal
        isOpen={isReelModalOpen}
        closeModal={() => setIsReelModalOpen(false)}
      >
        <div className="reel-list-modal">
          <h3>Reels</h3>
          <p className="reel-event">
            {
              eventData.find(
                (event) => event.pageId === selectedRequest.eventId
              )?.pageName
            }{" "}
            - {RequestType[selectedRequest.requestType]}
          </p>

          {selectedRequest.reels?.length > 0 ? (
            <table className="reel-table">
              <thead>
                <tr>
                  <th>BibId</th>
                  <th>Reel</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {selectedRequest.reels.map((reel, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <td>{reel?.bibId}</td>

                      <td>
                        <Button
                          title="Play"
                          onClick={() => handlePlayClick(reel.reelPath)}
                          disabled={!signedReels[reel.reelPath]}
                        >
                          <FontAwesomeIcon icon={faPlay} />
                        </Button>
                      </td>

                      <td>
                        <Button
                          title="Download"
                          onClick={() =>
                            window.open(signedReels[reel.reelPath], "_blank")
                          }
                          disabled={!signedReels[reel.reelPath]}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </Button>
                      </td>
                    </tr>

                    {expandedReelKey === reel.reelPath && (
                      <tr className="reel-expanded-row">
                        <td colSpan={3}>
                          <video
                            src={signedReels[reel.reelPath]}
                            controls
                            autoPlay
                            playsInline
                            style={{
                              width: "100%",
                              maxHeight: "360px",
                              borderRadius: "8px",
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No reels found.</p>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={isImageModalOpen}
        closeModal={() => setIsImageModalOpen(false)}
      >
        <div className="image-list-modal">
          <h3>Images</h3>

          <p className="reel-event">
            {
              eventData.find(
                (event) => event.pageId === selectedImageRequest?.eventId
              )?.pageName
            }
          </p>

          <div className="image-grid">
            {selectedImageRequest?.imageS3Keys?.map((key, index) => (
              <div key={index} className="image-card">
                <img
                  src={signedImages[key]}
                  alt="Event"
                  loading="lazy"
                  onClick={() => setPreviewImage(signedImages[key])}
                  style={{ cursor: "zoom-in" }}
                />

                <Button
                  title="Download"
                  onClick={() => window.open(signedImages[key], "_blank")}
                  disabled={!signedImages[key]}
                >
                  <FontAwesomeIcon icon={faDownload} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {previewImage && (
        <div
          className="image-lightbox"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="image-lightbox-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="lightbox-close"
              onClick={() => setPreviewImage(null)}
            >
              ✕
            </button>

            <img src={previewImage} alt="Preview" />
          </div>
        </div>
      )}

      <div className="gallery-wrapper">
        <div className="event-header">
          <h1>Processing Requests</h1>

          <div className="filter">
            <Button
              title="Refresh"
              onClick={fetchRequests}
              className="refresh-btn"
            >
              <FontAwesomeIcon icon={faRefresh} />
            </Button>
            <Input
              className="search-input"
              type="search"
              placeholder="Search"
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            <CustomDropdown
              options={[
                { label: "All Events", value: "" },
                ...eventData.map((event) => ({
                  label: event.pageName,
                  value: event.pageId,
                })),
              ]}
              className="event-dropdown"
              onSelect={(option) => handleEventFilter(option.value)}
              value={selectedEvent}
            />

            <Button
              onClick={() => navigate(-1)}
              className="back-btn"
              title="Back"
            >
              Back
            </Button>
          </div>
        </div>
        <div className="requests-table">
          <Table
            pagination={true}
            pageSize={10}
            totalRows={filteredRequests.length}
            categoryName="reel-requests"
          >
            <thead>
              <tr>
                <th onClick={() => handleSort("requestId")}>Request ID</th>
                <th onClick={() => handleSort("pageId")}>Event</th>
                <th onClick={() => handleSort("requestType")}>Request Type</th>
                <th onClick={() => handleSort("bibId")}>Bib Id</th>
                <th style={{ textAlign: "center" }}>Reels</th>
                <th style={{ textAlign: "center" }}>Images</th>
                <th onClick={() => handleSort("createdAt")}>Created At</th>
                <th onClick={() => handleSort("status")}>Status</th>
              </tr>
            </thead>

            <tbody>
              {filteredRequests.length ? (
                filteredRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((item) => (
                  <tr key={item.requestId}>
                    <td>{item.requestId}</td>
                    <td>
                      <a
                        href={`/events/clips/${item.eventId}?categoryId=${categoryId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {
                          eventData.find((event) => event.pageId === item.eventId)
                            ?.pageName
                        }
                      </a>
                    </td>
                    <td>{RequestType[item.requestType]}{RequestType[item.requestType] === "GENERATE REELS" && (item.bibId ? " (MANUAL)" : " (AUTO)")}{RequestType[item.requestType] === "PROCESS IMAGES" && (` (${item.processingMode?.toUpperCase()})`)}</td>
                    <td>{item.bibId ?? "—"}</td>
                    <td style={{ textAlign: "center" }}>
                      {item?.reels?.length ?
                        <span
                          className="reel-count-link"
                          onClick={() => handleOpenReelModal(item)}
                          style={{
                            cursor: "pointer",
                            color: "#1e88e5",
                            fontWeight: 600,
                          }}
                        >
                          {item?.reels?.length}
                        </span> : "—"
                      }
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {item?.imageS3Keys?.length ?
                        <span
                          className="reel-count-link"
                          onClick={() => handleOpenImageModal(item)}
                          style={{
                            cursor: "pointer",
                            color: "#1e88e5",
                            fontWeight: 600,
                          }}
                        >
                          {item?.imageS3Keys?.length}
                        </span> : "—"
                      }
                    </td>
                    <td>
                      {item.createdAt
                        ? moment(item.createdAt).format("DD MMM YYYY, hh:mm A")
                        : "—"}
                    </td>
                    <td>
                      {item.status === "PARTIAL_SUCCESS" ? (
                        <span
                          className="i"
                          title={item.error || ""}
                          style={{ cursor: "help" }}
                        >
                          i
                        </span>
                      ) : null}
                      <span className={`status ${item.status.toLowerCase()}`}>
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center" }}>
                    No requests found
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>
    </MainA>
  );
};

export default ReelRequest;
